import React from 'react'

import { Box } from '@mui/material'

import RadioCard from './RadioCard.component'

import styles from './RadioCardGroup.styles'
import type { RadioCardGroupProps } from './RadioCardGroup.types'

/**
 * Vertical group of selectable RadioCards (e.g. for ZIP selection: Airport ZIP vs custom ZIP).
 * Single selection; optional input slot per option (e.g. custom ZIP text field).
 */
const RadioCardGroup: React.FC<RadioCardGroupProps> = ({
  name,
  value,
  onChange,
  options,
  inputValue = '',
  onInputValueChange,
  direction = 'column',
  'aria-label': ariaLabel
}) => {
  const groupSx =
    direction === 'row'
      ? { ...styles.group, flexDirection: 'row' as const, maxWidth: 'none', alignItems: 'stretch' as const }
      : styles.group

  return (
    <Box role="radiogroup" aria-label={ariaLabel ?? undefined} sx={groupSx}>
      {options.map((option) => {
        const selected = value === option.value
        const inputNode =
          option.renderInput != null && selected && onInputValueChange != null
            ? option.renderInput({
                value: inputValue,
                onChange: onInputValueChange
              })
            : null

        const card = (
          <RadioCard
            key={option.value}
            name={name}
            value={option.value}
            title={option.title}
            description={option.description}
            selected={selected}
            disabled={option.disabled}
            onSelect={() => onChange(option.value)}
          >
            {inputNode}
          </RadioCard>
        )

        return direction === 'row' ? (
          <Box
            key={option.value}
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              '& > *': { flex: 1, minWidth: 0 }
            }}
          >
            {card}
          </Box>
        ) : (
          card
        )
      })}
    </Box>
  )
}

export default RadioCardGroup
